import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, Play, Shuffle } from "lucide-react";
import { historyIdx, syncLocation, useTrailEntries } from "@/lib/trail";
import { TopSearch } from "@/components/TopSearch";
import { ThemeButton } from "@/components/ThemeButton";
import { UserMenu } from "@/components/UserMenu";
import { useBarTitle } from "@/lib/barTitle";
import { ContinueChips } from "@/components/ContinueListening";

const isEditable = (el) => el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));

export const NavBar = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const entries = useTrailEntries();
  const bar = useBarTitle();
  useEffect(() => { syncLocation(pathname, search); }, [pathname, search]);
  const idx = historyIdx();
  const canBack = idx > 0;
  const canForward = idx < entries.length - 1;

  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || isEditable(document.activeElement)) return;
      if (e.key === "ArrowLeft" && canBack) { e.preventDefault(); navigate(-1); }
      if (e.key === "ArrowRight" && canForward) { e.preventDefault(); navigate(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canBack, canForward, navigate]);

  const trail = [];
  for (let i = idx; i >= 0; i--) {
    const e = entries[i];
    if (!e) break;
    trail.unshift({ ...e, delta: i - idx });
    if (e.root) break;
  }
  const parent = trail[0] && !trail[0].root && trail[trail.length - 1]?.parent;
  const crumbs = parent ? [{ ...parent, root: true, delta: null }, ...trail] : trail;
  const showTrail = crumbs.length > 1 && crumbs.every((c) => c.label);
  const isHome = pathname === "/albums" && search.includes("sort=recent");

  const btn = "flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-700 transition-colors hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-default disabled:opacity-30 disabled:hover:border-zinc-200";
  return (
    <div className="z-30 border-b border-zinc-300/80 bg-[#E8E5DC]/92 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_6px_18px_-12px_rgba(0,0,0,0.3)] backdrop-blur-md md:sticky md:top-0" data-testid="nav-bar">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-3 sm:gap-4 sm:px-10">
      <div className="flex shrink-0 items-center gap-1.5">
        <button type="button" onClick={() => navigate(-1)} disabled={!canBack} aria-label="Back" title="Back (Alt+←)" data-testid="nav-back" className={btn}><ChevronLeft size={16} /></button>
        <button type="button" onClick={() => navigate(1)} disabled={!canForward} aria-label="Forward" title="Forward (Alt+→)" data-testid="nav-forward" className={btn}><ChevronRight size={16} /></button>
      </div>
      {isHome && <ContinueChips />}
      {showTrail && !bar.past && !isHome && (
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 overflow-hidden text-sm" data-testid="breadcrumbs">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <span key={`${c.path}-${i}`} className="flex min-w-0 items-center gap-1.5">
                {i > 0 && <ChevronRight size={12} className="shrink-0 text-zinc-400" />}
                {last
                  ? <span className="truncate font-medium text-zinc-900" aria-current="page" data-testid={`crumb-${i}`}>{c.label}</span>
                  : <button type="button" onClick={() => (c.delta === null ? navigate(c.path) : navigate(c.delta))} data-testid={`crumb-${i}`}
                    className="truncate text-zinc-500 underline-offset-4 transition-colors hover:text-zinc-900 hover:underline">{c.label}</button>}
              </span>
            );
          })}
        </nav>
      )}
      <div aria-live="polite" className={`flex min-w-0 items-baseline gap-2 transition-all duration-300 ${bar.past && bar.title ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"}`}
        data-testid="nav-bar-title" data-visible={bar.past && bar.title ? "1" : "0"} style={bar.past && bar.title ? undefined : { width: 0, overflow: "hidden" }}>
        <span className="truncate font-heading text-base font-semibold text-zinc-900">{bar.title}</span>
        {bar.meta && <span className="hidden truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 sm:inline" data-testid="nav-bar-meta">{bar.meta}</span>}
        {bar.actions && (
          <span className="ml-2 flex shrink-0 items-center gap-1.5 self-center">
            <button type="button" onClick={bar.actions.play} disabled={bar.actions.disabled} aria-label="Play" title="Play" data-testid="nav-bar-play"
              className="flex h-8 items-center gap-1.5 rounded-full bg-zinc-900 px-3 text-xs font-medium text-white transition-transform hover:scale-[1.04] active:scale-95 disabled:opacity-30"><Play size={12} fill="currentColor" />Play</button>
            <button type="button" onClick={bar.actions.shuffle} disabled={bar.actions.disabled} aria-label="Shuffle" title="Shuffle" data-testid="nav-bar-shuffle"
              className={`${btn} px-0`}><Shuffle size={13} /></button>
            {bar.actions.onStar && (
              <button type="button" onClick={bar.actions.onStar} aria-label={bar.actions.starred ? "Remove from favourites" : "Add to favourites"} title="Favourite" data-testid="nav-bar-star" data-state={bar.actions.starred ? "on" : "off"}
                className={`${btn} px-0 ${bar.actions.starred ? "border-amber-300 text-amber-600 hover:text-amber-700" : ""}`}><Heart size={13} fill={bar.actions.starred ? "currentColor" : "none"} /></button>
            )}
          </span>
        )}
      </div>
      <TopSearch />
      <ThemeButton />
      <UserMenu />
      </div>
    </div>
  );
};
